import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Modal } from '@components/Modal';
import { fetchPreferenceCenterUrl } from '../../../../api/preferences';
import { IGetSubscriptionDetailsResponse } from '../../../../api/subscription';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import { CustomSelect } from '../../../../components/CustomSelect';
import { MailIcon } from '../../../../components/Icons';
import { Features } from '../../../../config';
import { organization } from '../../../../types/oidcUser';
import { getInitialsFromEmail } from '../../../../utils/authUtils';

interface OverviewTabProps {
  userFirstName?: string;
  userLastName?: string;
  email: string;
  isFetching: boolean;
  subscriptionDetails?: IGetSubscriptionDetailsResponse;
  onSubmitForm: (data: { firstName: string; lastName: string }) => void;
  onUpdateDefaultOrganization: (organizationId: string) => Promise<void>;
  isUpdating?: boolean;
  organizations?: organization[];
  defaultOrgId: string;
  isUpdatingDefaultOrganization: boolean;
}

type Inputs = {
  firstName: string;
  lastName: string;
};

export default function OverviewTab({
  userFirstName,
  userLastName,
  email,
  isFetching,
  subscriptionDetails,
  onSubmitForm,
  onUpdateDefaultOrganization,
  isUpdating,
  organizations,
  defaultOrgId,
  isUpdatingDefaultOrganization,
}: OverviewTabProps) {
  const [selectedOrg, setSelectedOrg] = useState(defaultOrgId);
  const { t } = useTranslation();
  const [emailPrefsLoading, setEmailPrefsLoading] = useState(false);
  const [prefsPopupBlockedOpen, setPrefsPopupBlockedOpen] = useState(false);
  const [prefsPopupBlockedUrl, setPrefsPopupBlockedUrl] = useState('');
  const nameAbbr =
    userFirstName && userLastName
      ? `${userFirstName[0]}${userLastName[0]}`.toUpperCase()
      : getInitialsFromEmail(email);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      firstName: userFirstName || '',
      lastName: userLastName || '',
    },
  });

  useEffect(() => {
    if (!isUpdating) {
      reset({
        firstName: userFirstName || '',
        lastName: userLastName || '',
      });
    }
  }, [isUpdating]);

  useEffect(() => {
    setValue('firstName', userFirstName || '', { shouldValidate: true });
    setValue('lastName', userLastName || '', { shouldValidate: true });
  }, [userFirstName, userLastName, setValue]);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    onSubmitForm({
      firstName: data.firstName,
      lastName: data.lastName,
    });
  };

  const billingProvider = subscriptionDetails?.billingDetails?.provider;

  const currentOrganization = organizations?.find(
    (org) => org.id === defaultOrgId,
  );
  const isOwner = currentOrganization?.isOwner ?? false;

  const submitButtonClassName =
    'h-[44px] px-6 bg-white hover:bg-[#F2F2F2] transition-colors duration-200 text-black rounded-[16px] font-medium font-[NapsterSans] whitespace-nowrap disabled:!cursor-not-allowed disabled:opacity-70 flex items-center justify-center gap-2';

  const emailPrefsSecondaryButtonClassName =
    'h-[44px] px-6 bg-white/10 hover:bg-white/15 border border-white/20 transition-colors duration-200 text-white rounded-[16px] font-medium font-[NapsterSans] whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed';

  const handleManageEmailPreferences = async () => {
    setEmailPrefsLoading(true);
    const newTab = window.open('about:blank', '_blank');

    try {
      const body = await fetchPreferenceCenterUrl();
      if (!body.success || !body.data?.url) {
        newTab?.close();
        toast.error(t('account.unableToLoadEmailPrefs'));
        return;
      }
      const url = body.data.url;

      if (newTab) {
        newTab.location.href = url;
      } else {
        setPrefsPopupBlockedUrl(url);
        setPrefsPopupBlockedOpen(true);
      }
    } catch {
      newTab?.close();
      toast.error(t('account.unableToLoadEmailPrefs'));
    } finally {
      setEmailPrefsLoading(false);
    }
  };

  const closePrefsPopupBlockedModal = () => {
    setPrefsPopupBlockedOpen(false);
    setPrefsPopupBlockedUrl('');
  };

  return (
    <>
      <div className="rounded-regular p-6 tc-mv-blur text-white">
        <div className="flex sm:items-center items-start gap-5 flex-wrap flex-col sm:flex-row ">
          <div
            className={`flex gap-4 overflow-hidden sm:w-auto w-full justify-start`}>
            <div className="flex-shrink-0 w-[56px] h-[56px] sm:h-[72px] sm:w-[72px] !rounded-full tc-mv-blur flex items-center justify-center text-xl sm:text-2xl font-medium">
              {nameAbbr}
            </div>
            <div className="text-left overflow-hidden flex flex-col justify-center">
              <h2 className="text-2xl font-medium break-all">
                {isFetching ? (
                  <div className="max-w-64 h-[32px] tc-mv-blur rounded animate-pulse" />
                ) : (
                  `${userFirstName || ''} ${userLastName || ''} `
                )}
              </h2>
              <div className="flex items-baseline gap-2 text-lg">
                <div
                  className={` ${
                    isFetching ? 'flex items-center mt-1 ' : 'w-full truncate'
                  }`}>
                  <MailIcon className="inline me-2" />
                  {isFetching ? (
                    <div className="w-64 h-[22px] tc-mv-blur rounded animate-pulse" />
                  ) : (
                    email
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="sm:ml-auto ml-[72px] flex items-center justify-between gap-4 sm:flex-row flex-col sm:text-right text-left">
            <div>
              <div className="text-[12px] text-[#C1C1CA]">
                {t('account.yourCurrentPlan')}
              </div>
              <div
                className="text-2xl font-medium"
                data-testid="plan-name-display">
                {isFetching ? (
                  <AnimatedSpinner />
                ) : (
                  subscriptionDetails?.billingDetails?.planName
                )}
              </div>
            </div>
            {(billingProvider === 'stripe' ||
              billingProvider === 'recurly' ||
              billingProvider === 'none') &&
              isOwner && (
                <Link
                  to="/pricing"
                  className="h-[44px] px-6 bg-white hover:bg-[#F2F2F2] transition-colors duration-200 text-black rounded-[16px] font-medium font-[NapsterSans] whitespace-nowrap flex items-center justify-center">
                  {t('account.upgrade')}
                </Link>
              )}
          </div>
        </div>
      </div>
      {organizations &&
        organizations.length > 1 &&
        Features.enableOrganizationManagement && (
          <div className="rounded-regular p-6 tc-mv-blur mt-4 text-white text-left relative z-10">
            <h5 className="mb-4">{t('account.selectDefaultOrg')}</h5>
            <CustomSelect
              className="w-full"
              options={
                organizations?.map((org) => ({
                  label: org.name,
                  value: org.id,
                  badge: org.isOwner ? 'Owner' : undefined,
                })) ?? []
              }
              value={selectedOrg}
              onChange={setSelectedOrg}
              placeholder={t('account.selectOrgPlaceholder')}
            />
            <div className="mt-6 flex justify-end">
              <button
                disabled={
                  selectedOrg === defaultOrgId || isUpdatingDefaultOrganization
                }
                onClick={() => onUpdateDefaultOrganization(selectedOrg)}
                className={submitButtonClassName}>
                {isUpdatingDefaultOrganization && (
                  <span className="ms-2">
                    <AnimatedSpinner />
                  </span>
                )}
                {t('account.saveOrganization')}
              </button>
            </div>
          </div>
        )}

      <div className="rounded-regular p-6 tc-mv-blur mt-4 text-white text-left">
        <h5>{t('account.profileDetails')}</h5>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label htmlFor="firstName" className="block mt-4 text-[10px] ">
            {t('account.firstName')}
          </label>
          <input
            id="firstName"
            data-testid="first-name-input"
            type="text"
            className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2"
            autoComplete="off"
            placeholder={t('account.firstNamePlaceholder')}
            {...register('firstName', {
              required: true,
              validate: (value) => value.trim().length > 0,
            })}
          />
          <label htmlFor="lastName" className="block mt-4 text-[10px] ">
            {t('account.lastName')}
          </label>
          <input
            id="lastName"
            data-testid="last-name-input"
            type="text"
            className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2"
            autoComplete="off"
            placeholder={t('account.lastNamePlaceholder')}
            {...register('lastName', {
              required: true,
              validate: (value) => value.trim().length > 0,
            })}
          />
          <label htmlFor="email" className="block mt-4 text-[10px] ">
            {t('account.email')}
          </label>
          <input
            id="email"
            data-testid="email-input"
            type="email"
            className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2 opacity-50"
            defaultValue={email}
            disabled
          />
          <div className="mt-6 flex justify-end">
            <button
              disabled={!isValid || isUpdating || !isDirty}
              type="submit"
              className={submitButtonClassName}>
              {t('account.saveChanges')}
              {isUpdating && (
                <span className="ms-2">
                  <AnimatedSpinner />
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-regular p-6 tc-mv-blur mt-4 text-white text-left">
        <h5>{t('account.emailPreferences')}</h5>
        <p className="mt-2 text-sm text-white/60">
          {t('account.emailPreferencesDescription')}
        </p>
        <button
          type="button"
          onClick={handleManageEmailPreferences}
          disabled={emailPrefsLoading}
          className={`${emailPrefsSecondaryButtonClassName} mt-4`}>
          {t('account.managePreferences')}
          {emailPrefsLoading ? (
            <span className="ms-2">
              <AnimatedSpinner />
            </span>
          ) : null}
        </button>
      </div>

      <Modal
        isOpen={prefsPopupBlockedOpen}
        onClose={closePrefsPopupBlockedModal}
        title={t('account.openEmailPreferences')}
        maxWidth="max-w-md">
        <p className="text-sm text-white/80">
          {t('account.popupBlockedMessage')}
        </p>
        {prefsPopupBlockedUrl ? (
          <a
            href={prefsPopupBlockedUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closePrefsPopupBlockedModal}
            className={`${emailPrefsSecondaryButtonClassName} mt-6 inline-flex w-full justify-center no-underline`}>
            {t('account.openInNewTab')}
          </a>
        ) : null}
      </Modal>
    </>
  );
}
